import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePost, usePostComments } from '../hooks/usePosts';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageCircle, Share, LogIn } from 'lucide-react';
import CommentsModal from '@/components/CommentsModal';
import ShareModal from '@/components/ShareModal';
import LikeButton from '@/components/LikeButton';
import { useTranslation } from 'react-i18next';
import MediaCarousel from '@/components/MediaCarousel';
import AppLayout from '@/components/navigation/AppLayout';
import { PostMetaTags } from '@/components/PostMetaTags';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';

const PostDetail = () => {
  const { postId } = useParams<{ postId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);

  const { data: post, isLoading, error } = usePost(postId);
  const { data: comments } = usePostComments(postId);
  
  // Check if post is private and user is not authenticated
  const isPrivatePost = post && post.privacy_setting !== 'public';
  const canViewPost = !isPrivatePost || (user && post?.user_id === user.id);

  
  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  
  if (error) {
    return (
      <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-red-500 mb-4">Este post no está disponible</p>
            <Button asChild>
              <Link to="/">Volver al inicio</Link>
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show error if post is private and user can't view it
  if (post && !canViewPost) {
    return (
      <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold mb-2">Post Privado</h2>
            <p className="text-muted-foreground mb-4">
              Este post no está disponible públicamente. Solo el dueño puede verlo.
            </p>
            <div className="flex gap-2 justify-center">
              <Button asChild>
                <Link to="/">Volver al inicio</Link>
              </Button>
              {!user && (
                <Button asChild variant="outline">
                  <Link to="/auth">
                    <LogIn className="h-4 w-4 mr-2" />
                    Iniciar sesión
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  



  return (
    <AppLayout headerTitle={t('titles.postDetail')} showBackButton={true}>
      {post && <PostMetaTags post={post} />}
      
      <div className="w-full max-w-full overflow-x-hidden">
      {/* Show login prompt for guests */}
      {!user && post && (
        <Alert className="m-4 bg-blue-50 border-blue-200">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 break-words">Inicia sesión para comentar y dar like</span>
            <Button size="sm" asChild className="shrink-0">
              <Link to="/auth">
                <LogIn className="h-4 w-4 mr-2" />
                Iniciar sesión
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex-1 w-full max-w-full min-w-0 overflow-x-hidden break-words">
        {isLoading ? (
          <div className="p-4">
            <div className="flex items-center mb-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="ml-3">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <Skeleton className="h-64 w-full mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : post ? (
          <div>
            {/* Post header */}
            <div className="p-4 flex items-center">
              <Link to={`/pet/${post.pet_id}`} className="flex items-center">
                <div className={`mr-3 ${post.pet?.is_memorial ? 'memorial-avatar-halo rounded-full' : ''}`}>
                  <Avatar className="h-10 w-10" key={post.pet?.photo_url || post.pet_id}>
                    {post.pet?.photo_url ? (
                      <AvatarImage 
                        src={post.pet.photo_url} 
                        alt={post.pet?.name || 'Pet'}
                        loading="eager"
                      />
                    ) : (
                      <AvatarFallback>{post.pet?.name?.charAt(0) || '🐾'}</AvatarFallback>
                    )}
                  </Avatar>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">
                      {post.pet?.name || 'Unknown Pet'}
                    </p>
                    {post.pet?.is_memorial && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs italic text-white bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                        {t('common.foreverLoved')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {post.created_at
                      ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
                      : 'recently'}
                  </p>
                </div>
              </Link>
            </div>

            {/* Post media - Carousel */}
            {post.media && post.media.length > 0 && (
              <div className={`relative w-full max-h-[75vh] overflow-hidden bg-gray-100 flex items-center justify-center ${post.pet?.is_memorial ? 'memorial-photo-frame' : ''}`}>
                <MediaCarousel
                  media={post.media}
                  alt={post.caption || 'Post'}
                  containerClassName="max-h-[75vh]"
                />
              </div>
            )}

            {/* Post actions */}
            <div className="px-4 py-3 border-t border-b border-gray-100 flex items-center space-x-3">
              {user ? (
                <LikeButton 
                  id={postId!}
                  showModal={true}
                />
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex items-center opacity-50 cursor-not-allowed"
                  disabled
                  title="Inicia sesión para dar like"
                >
                  <span>❤️</span>
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center"
                onClick={() => setIsCommentsModalOpen(true)}
              >
                <MessageCircle className="h-5 w-5 mr-1" />
                <span>{comments?.length || 0}</span>
              </Button>

              
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex items-center"
                onClick={handleShare}
              >
                <Share className="h-5 w-5 mr-1" />
                <span>Share</span>
              </Button>
            </div>

            {/* Post caption */}
            {post.caption && (
              <div className="p-4 pb-2">
                <p className="mb-1 font-medium">{post.pet?.name || 'Unknown Pet'}</p>
                <p>{post.caption}</p>
              </div>
            )}

          </div>
        ) : null}
      </div>
      </div>

      {/* Comments Modal */}
      {post && (
        <CommentsModal
          isOpen={isCommentsModalOpen}
          onClose={() => setIsCommentsModalOpen(false)}
          postId={postId!}
          petName={post.pet?.name}
        />
      )}




      {/* Share Modal */}
      {post && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          postId={postId!}
          petName={post.pet?.name}
        />
      )}
    </AppLayout>
  );
};

export default PostDetail;
